import React, { useEffect, useState } from "react";
import { ArrowLeft, Download, FileText, Loader2 } from "lucide-react";
import api, { API } from "../lib/api";
import Flipbook from "../components/Flipbook";
import { LangMenu } from "../i18n";
import { journey } from "../lib/journey";

// 4 Nov 2026 (RK): read the ₹249 Release Kit online like the ebook, download it, and go back to the course offer.
const G = "#C88A24", IVORY = "#F3EFE7", MUTED = "#B7B0A5";

export default function BlueprintKit() {
    const token = new URLSearchParams(window.location.search).get("token") || "";
    const [d, setD] = useState(null);
    const [state, setState] = useState("loading");
    useEffect(() => {
        if (!token) { setState("denied"); return; }
        api.get(`/blueprint/kit-pages/${token}`).then((r) => { setD(r.data); setState("ok"); }).catch(() => setState("denied"));
    }, [token]);
    const full = d?.files?.find((f) => f.name.startsWith("Release Kit"));
    const printables = (d?.files || []).filter((f) => !f.name.startsWith("Release Kit"));
    const back = d?.back || `/blueprint/read?token=${encodeURIComponent(token)}`;
    const dl = (f) => `${API}/blueprint/kit/${encodeURIComponent(token)}/${encodeURIComponent(f.name)}`;

    if (state === "loading") return (
        <div className="min-h-screen grid place-items-center bg-[#080808]" style={{ color: IVORY }}>
            <div className="text-center"><Loader2 className="h-8 w-8 animate-spin mx-auto" /><p className="mt-4 text-xs tracking-[0.3em]" style={{ color: MUTED }}>OPENING YOUR RELEASE KIT…</p></div>
        </div>
    );
    if (state === "denied") return (
        <div className="min-h-screen grid place-items-center bg-[#080808] text-center px-6" style={{ color: IVORY }}>
            <div>
                <p className="text-xs tracking-[0.35em] mb-4" style={{ color: G }}>ACCESS REQUIRED</p>
                <h1 className="font-display font-black text-3xl">The Release Kit is for its buyers</h1>
                <p className="mt-3" style={{ color: MUTED }}>This link doesn't include the Release Kit. Open your ebook from your email link.</p>
                <a href="/blueprint" className="mt-6 inline-flex items-center gap-2 px-6 py-3 font-bold rounded-sm" style={{ background: G, color: "#080808" }}>Go to the Blueprint</a>
            </div>
        </div>
    );
    return (
        <div className="min-h-screen flex flex-col bg-[#080808]" style={{ color: IVORY, backgroundImage: "radial-gradient(120% 80% at 50% 0%, #1c130a 0%, #080808 60%)" }} data-testid="blueprint-kit">
            <header className="flex items-center justify-between gap-3 px-4 sm:px-8 h-16 border-b border-[#C88A24]/20 sticky top-0 bg-[#080808]/90 backdrop-blur z-10">
                <a href={back} className="inline-flex items-center gap-2 text-sm whitespace-nowrap" style={{ color: MUTED }} data-testid="kit-back"><ArrowLeft className="h-4 w-4" /> {back.includes("/blueprint/course") ? "Video course" : "Ebook"}</a>
                <p className="font-display font-black text-sm sm:text-base truncate">The Release Kit</p>
                <LangMenu dark className="ml-auto" />
                {full && <a href={dl(full)} className="inline-flex items-center gap-1.5 px-4 py-2 text-xs font-bold rounded-sm" style={{ background: G, color: "#080808" }} data-testid="kit-download" data-track="Download Release Kit PDF" onClick={() => journey("ebook_download", "release kit")}><Download className="h-4 w-4" /> PDF</a>}
            </header>
            <div className="text-center px-4 pt-4 text-sm" style={{ color: MUTED }}>
                {d?.name ? <>Your Release Kit, {d.name}. Swipe to turn the pages.</> : <>Your Release Kit. Swipe to turn the pages.</>}
            </div>
            <div className="flex-1 flex items-center justify-center px-2 sm:px-6 py-6">
                {d?.pages?.length ? <Flipbook pages={d.pages} ratio={1.5} /> : <p style={{ color: MUTED }}>Pages unavailable. Use the download buttons below.</p>}
            </div>
            <section className="px-4 sm:px-8 pb-12">
                <div className="max-w-3xl mx-auto rounded-2xl p-5 sm:p-7 text-center sm:text-left" style={{ border: "1px solid rgba(200,138,36,.35)", background: "#120d08" }}>
                    <p className="text-[11px] tracking-[0.3em]" style={{ color: G }}>DOWNLOAD</p>
                    <h2 className="mt-1 font-display font-black text-xl sm:text-2xl">Keep it on your phone or print it</h2>
                    <div className="mt-4 grid sm:grid-cols-2 gap-2 text-left">
                        {full && <a href={dl(full)} className="flex items-center gap-3 rounded-xl px-4 py-3 text-sm hover:bg-[#1a120a]" style={{ border: "1px solid rgba(200,138,36,.45)" }}><Download className="h-4 w-4 shrink-0" style={{ color: G }} /><span className="font-bold">The Release Kit (full designed PDF)</span></a>}
                        {printables.map((f) => (
                            <a key={f.name} href={dl(f)} className="flex items-center gap-3 rounded-xl px-4 py-3 text-sm hover:bg-[#1a120a]" style={{ border: "1px solid rgba(255,255,255,.08)" }} data-testid="kit-file">
                                <FileText className="h-4 w-4 shrink-0" style={{ color: G }} /><span>{f.name.replace(/^\d+ - /, "").replace(/\.pdf$/i, "")} · printable</span>
                            </a>
                        ))}
                    </div>
                    <a href={back} className="mt-5 inline-flex items-center gap-2 text-sm underline underline-offset-4" style={{ color: MUTED }}><ArrowLeft className="h-4 w-4" /> {back.includes("/blueprint/course") ? "Back to the video course" : "Back to your ebook"}</a>
                </div>
            </section>
        </div>
    );
}
